<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1">Reservations</h1>
            <p class="text-muted mb-0">
              Bookings for your rental products, with housekeeping and laundry costs
            </p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="openAddReservation">
              <CIcon name="cil-plus" /> Add reservation
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Filters - collapsible -->
    <CCard class="filter-card">
      <CCardHeader class="d-flex justify-content-between align-items-center">
        <div>
          <CIcon name="cil-filter" />
          <span class="font-weight-bold ml-1">Filters</span>
          <CBadge color="primary" class="ml-2" v-if="activeFilterCount > 0">
            {{ activeFilterCount }} active
          </CBadge>
        </div>
        <CButton
          color="link"
          class="p-0"
          :aria-expanded="showFilters ? 'true' : 'false'"
          :aria-label="showFilters ? 'Hide filters' : 'Show filters'"
          @click="showFilters = !showFilters"
        >
          <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-chevron-bottom'" />
        </CButton>
      </CCardHeader>
      <CCollapse :show.sync="showFilters" :duration="300">
        <CCardBody>
          <CRow>
            <CCol md="3">
              <CSelect
                :value.sync="filterCriteria.rentalProductId"
                :options="filterRentalProducts"
                label="Rental product"
              />
            </CCol>
            <CCol md="2">
              <CInput label="From" type="date" v-model="filterCriteria.startDate" />
            </CCol>
            <CCol md="2">
              <CInput label="To" type="date" v-model="filterCriteria.endDate" />
            </CCol>
            <CCol md="2">
              <CSelect
                :value.sync="filterCriteria.cleaningIsPaid"
                :options="[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Paid' },
                  { value: 'false', label: 'Unpaid' },
                ]"
                label="Housekeeping payment"
              />
            </CCol>
            <CCol md="1">
              <CInput
                label="Days"
                type="number"
                inputmode="numeric"
                v-model="filterCriteria.totalDays"
              />
            </CCol>
            <CCol md="2" class="d-flex align-items-end pb-3">
              <CButton color="light" @click="onCleanerClick" class="mr-2">
                Clear
              </CButton>
              <CButton color="primary" variant="outline" @click="refreshTable">
                Search
              </CButton>
            </CCol>
          </CRow>
          <CRow v-if="filterCriteria.totalDays">
            <CCol sm="6">
              <div class="d-flex align-items-center">
                <CSwitch
                  class="mr-2"
                  color="primary"
                  :checked.sync="filterCriteria.minDaysFilter"
                  aria-label="Treat days as a minimum"
                />
                <span>Treat days as a minimum (otherwise an exact match)</span>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCollapse>
    </CCard>

    <!-- Mobile view toggle -->
    <div class="d-md-none mb-3">
      <CButtonGroup class="w-100" role="group" aria-label="View">
        <CButton
          color="primary"
          :variant="mobileView === 'list' ? undefined : 'outline'"
          :aria-pressed="mobileView === 'list' ? 'true' : 'false'"
          @click="mobileView = 'list'"
        >
          <CIcon name="cil-list" /> Table
        </CButton>
        <CButton
          color="primary"
          :variant="mobileView === 'calendar' ? undefined : 'outline'"
          :aria-pressed="mobileView === 'calendar' ? 'true' : 'false'"
          @click="mobileView = 'calendar'"
        >
          <CIcon name="cil-calendar" /> Calendar
        </CButton>
      </CButtonGroup>
    </div>

    <!-- Split view layout -->
    <CRow class="split-view-container">
      <!-- Left panel: calendar -->
      <CCol
        lg="4"
        md="5"
        class="calendar-panel"
        :class="{ 'd-none d-md-block': mobileView === 'list' }"
      >
        <CCard>
          <CCardHeader>
            <h2 class="h6 mb-0">Calendar</h2>
          </CCardHeader>
          <CCardBody class="p-2">
            <!-- Colour legend -->
            <div class="color-legend mb-2">
              <div
                v-for="product in rentalProductsList"
                :key="product.id"
                class="legend-item"
              >
                <span
                  class="color-dot border"
                  :style="{ backgroundColor: product.colorCode }"
                  aria-hidden="true"
                ></span>
                <span class="small">{{ product.name }}</span>
              </div>
            </div>

            <calendar-view
              id="calendar"
              :events="events"
              enable-drag-drop
              :show-date="showDate"
              :displayPeriodUom="displayPeriod"
              :class="themeClasses"
              currentPeriodLabel="icons"
              @click-date="onClickDay"
              @click-event="onClickEvent"
              @drop-on-date="onDrop"
              @show-date-change="setShowDate"
              ref="calendarView"
            >
              <calendar-view-header
                slot="header"
                slot-scope="{ headerProps }"
                :header-props="headerProps"
                @input="setShowDate"
              />
            </calendar-view>
            <small class="form-text text-muted px-1">
              Click a start day and then an end day to add a reservation. Click a booking to edit it.
            </small>
          </CCardBody>
          <CCardFooter class="py-2">
            <CSelect
              :options="viewOptions"
              :value.sync="displayPeriod"
              label="View"
              horizontal
              class="mb-0"
            />
          </CCardFooter>
        </CCard>
      </CCol>

      <!-- Right panel: list -->
      <CCol
        lg="8"
        md="7"
        class="list-panel"
        :class="{ 'd-none d-md-block': mobileView === 'calendar' }"
      >
        <CCard>
          <CCardHeader class="d-flex align-items-center">
            <h2 class="h6 mb-0">Reservations</h2>
            <span class="ml-2 text-muted small" v-if="items.length > 0">
              {{ items.length }} {{ items.length === 1 ? "reservation" : "reservations" }}
            </span>
            <span class="ml-auto small" v-if="selectedReservations.length > 0">
              {{ selectedReservations.length }} selected
            </span>
          </CCardHeader>
          <CCardBody class="p-0">
            <CProgress v-if="loading && items.length" :value="100" animated size="xs" color="primary" />
            <div v-if="loading && !items.length" class="text-center p-5">
              <CSpinner color="primary" />
            </div>
            <div v-else-if="error && !items.length" class="text-center p-5">
              <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
              <h3 class="h6">Couldn't load reservations</h3>
              <p class="text-muted">Check your connection and try again.</p>
              <CButton color="primary" variant="outline" @click="refreshTable">
                <CIcon name="cil-reload" /> Retry
              </CButton>
            </div>
            <div v-else-if="!items.length && activeFilterCount === 0" class="text-center p-5">
              <CIcon name="cil-calendar" size="xl" class="text-muted mb-2" />
              <h3 class="h6">No reservations yet</h3>
              <p class="text-muted">
                Add a booking here, or pick its days on the calendar.
              </p>
              <CButton color="primary" @click="openAddReservation">
                <CIcon name="cil-plus" /> Add reservation
              </CButton>
            </div>
            <div v-else-if="!items.length" class="text-center p-4">
              <h3 class="h6">No reservations match your filters</h3>
              <CButton color="light" @click="onCleanerClick">
                <CIcon name="cil-x" /> Clear filters
              </CButton>
            </div>
            <template v-else>
              <div class="table-wrapper d-none d-lg-block">
                <CDataTable
                  :items="computedItems"
                  :fields="fields"
                  class="reservation-table mb-0"
                  hover
                  striped
                  small
                >
                  <template #show_select-header>
                    <CInputCheckbox
                      :checked="isAllSelected"
                      aria-label="Select all reservations"
                      @change="toggleSelectAll"
                    />
                  </template>

                  <template #show_select="{ item }">
                    <td
                      class="py-2"
                      :ref="`row-${item.id}`"
                      :class="{ 'highlighted-row': highlightedItemId === item.id }"
                      @click="highlightListRow(item.id)"
                    >
                      <CInputCheckbox
                        :checked="selectedReservations.includes(item.id)"
                        :aria-label="`Select ${item.customerName}`"
                        @change="toggleSelection(item.id)"
                      />
                    </td>
                  </template>

                  <template #show_index="{ index }">
                    <td class="py-2">
                      {{ index + 1 }}
                    </td>
                  </template>

                  <template #cleaningIsPaid="{ item }">
                    <td>
                      <CBadge :color="item.cleaningIsPaidRaw ? 'success' : 'warning'">
                        {{ item.cleaningIsPaid }}
                      </CBadge>
                    </td>
                  </template>
                  <template #laundryIsPaid="{ item }">
                    <td>
                      <CBadge :color="item.laundryIsPaidRaw ? 'success' : 'warning'">
                        {{ item.laundryIsPaid }}
                      </CBadge>
                    </td>
                  </template>
                  <template #cleaningCostInRM="{ item }">
                    <td class="text-right">{{ item.cleaningCostInRM }}</td>
                  </template>
                  <template #laundryCostInRM="{ item }">
                    <td class="text-right">{{ item.laundryCostInRM }}</td>
                  </template>
                  <template #totalCostDisplay="{ item }">
                    <td class="text-right">{{ item.totalCostDisplay }}</td>
                  </template>

                  <template #show_details="{ item }">
                    <td class="py-2 action-buttons">
                      <CButton
                        color="primary"
                        variant="ghost"
                        :aria-label="`Edit ${item.customerName}'s reservation`"
                        @click="onEdit(item)"
                      >
                        <CIcon name="cil-pencil" />
                      </CButton>
                      <CButton
                        color="primary"
                        variant="ghost"
                        :aria-label="`Duplicate ${item.customerName}'s reservation`"
                        @click="onDuplicate(item)"
                      >
                        <CIcon name="cil-copy" />
                      </CButton>
                      <CButton
                        color="danger"
                        variant="ghost"
                        :aria-label="`Delete ${item.customerName}'s reservation`"
                        @click="showDeleteConfirmation(item)"
                      >
                        <CIcon name="cil-trash" />
                      </CButton>
                    </td>
                  </template>
                  <template #footer>
                    <tr>
                      <td colspan="6" class="font-weight-bold">Totals</td>
                      <td class="font-weight-bold">{{ totalDays }}</td>
                      <td colspan="1"></td>
                      <td class="text-right font-weight-bold">RM {{ formatMyr(totalCleaningCost) }}</td>
                      <td colspan="1"></td>
                      <td class="text-right font-weight-bold">RM {{ formatMyr(totalLaundryCost) }}</td>
                      <td class="text-right font-weight-bold">RM {{ formatMyr(totalCost) }}</td>
                      <td colspan="1"></td>
                    </tr>
                  </template>
                </CDataTable>
              </div>
              <!-- Card list below lg -->
              <div class="d-lg-none p-2">
                <CCard
                  v-for="item in computedItems"
                  :key="item.id"
                  class="mb-2"
                  :class="{ 'highlighted-row': highlightedItemId === item.id }"
                >
                  <CCardBody class="p-3">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="d-flex align-items-start">
                        <CInputCheckbox
                          :checked="selectedReservations.includes(item.id)"
                          :aria-label="`Select ${item.customerName}`"
                          @change="toggleSelection(item.id)"
                        />
                        <div>
                          <div class="font-weight-bold">{{ item.customerName }}</div>
                          <div class="small text-muted">{{ item.productName }}</div>
                          <div class="small">
                            {{ item.startDate }} – {{ item.endDate }}
                            ({{ item.totalDays }} {{ item.totalDays === 1 ? "day" : "days" }})
                          </div>
                        </div>
                      </div>
                      <div class="font-weight-bold text-nowrap">{{ item.totalCostDisplay }}</div>
                    </div>
                    <div class="mt-2 d-flex flex-wrap align-items-center">
                      <span class="small mr-1">Housekeeping</span>
                      <CBadge :color="item.cleaningIsPaidRaw ? 'success' : 'warning'" class="mr-3">
                        {{ item.cleaningIsPaid }}
                      </CBadge>
                      <span class="small mr-1">Laundry</span>
                      <CBadge :color="item.laundryIsPaidRaw ? 'success' : 'warning'">
                        {{ item.laundryIsPaid }}
                      </CBadge>
                      <div class="ml-auto text-nowrap">
                        <CButton
                          color="primary"
                          variant="ghost"
                          :aria-label="`Edit ${item.customerName}'s reservation`"
                          @click="onEdit(item)"
                        >
                          <CIcon name="cil-pencil" />
                        </CButton>
                        <CButton
                          color="primary"
                          variant="ghost"
                          :aria-label="`Duplicate ${item.customerName}'s reservation`"
                          @click="onDuplicate(item)"
                        >
                          <CIcon name="cil-copy" />
                        </CButton>
                        <CButton
                          color="danger"
                          variant="ghost"
                          :aria-label="`Delete ${item.customerName}'s reservation`"
                          @click="showDeleteConfirmation(item)"
                        >
                          <CIcon name="cil-trash" />
                        </CButton>
                      </div>
                    </div>
                  </CCardBody>
                </CCard>
              </div>
            </template>
          </CCardBody>
          <CCardFooter class="py-2">
            <div class="d-flex flex-wrap gap-2">
              <CButton color="light" @click="selectAllFiltered">
                Select all
              </CButton>
              <CButton
                color="light"
                :disabled="selectedReservations.length === 0"
                @click="deselectAll"
              >
                Clear selection
              </CButton>
              <CButton
                color="primary"
                variant="outline"
                :disabled="selectedReservations.length === 0"
                @click="openBatchEditModal"
              >
                <CIcon name="cil-pencil" /> Edit selected
              </CButton>
              <CButton color="secondary" variant="outline" @click="exportTableAsImage">
                <CIcon name="cil-cloud-download" /> Export
              </CButton>
              <CButton color="secondary" variant="outline" @click="importFromICal">
                <CIcon name="cil-cloud-upload" /> Import
              </CButton>
              <CButton
                color="primary"
                :disabled="selectedReservations.length === 0"
                @click="openCreateBillModal"
              >
                <CIcon name="cil-file" /> Create bill
              </CButton>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
    <!-- End split view -->
    <div>
      <CModal
        size="lg"
        :show.sync="addReservationFormPopup"
        :close-on-backdrop="false"
        :centered="true"
      >
        <template #header>
          <h5 class="modal-title">
            {{ itemReservationTemp.id ? "Edit reservation" : "Add reservation" }}
          </h5>
          <CButtonClose aria-label="Close" @click="cancelReservation" />
        </template>
        <CRow form>
          <CCol sm="12">
            <CSelect
              :value.sync="itemReservationTemp.rentalProductId"
              :options="rentalProducts"
              label="Rental product *"
            />
          </CCol>
          <CCol sm="6">
            <CInput
              label="From *"
              type="date"
              v-model="itemReservationTemp.startDate"
              :is-valid="reservationErrors.startDate ? false : null"
              :invalid-feedback="reservationErrors.startDate"
            />
          </CCol>
          <CCol sm="6">
            <CInput
              label="To *"
              type="date"
              v-model="itemReservationTemp.endDate"
              :is-valid="reservationErrors.endDate ? false : null"
              :invalid-feedback="reservationErrors.endDate"
            />
          </CCol>
          <CCol sm="6">
            <CInput
              label="Guest name *"
              v-model="itemReservationTemp.customerName"
              :is-valid="reservationErrors.customerName ? false : null"
              :invalid-feedback="reservationErrors.customerName"
            />
          </CCol>
          <CCol sm="6">
            <CInput
              label="Guest phone (optional)"
              type="tel"
              placeholder="012-345 6789"
              v-model="itemReservationTemp.customerPhone"
            />
          </CCol>
          <CCol sm="6">
            <CInput
              label="Price"
              type="number"
              inputmode="decimal"
              prepend="RM"
              v-model="itemReservationTemp.price"
            />
          </CCol>
          <CCol sm="6">
            <CInput
              label="Housekeeping cost"
              type="number"
              inputmode="decimal"
              prepend="RM"
              v-model="itemReservationTemp.cleaningCost"
            />
          </CCol>
          <CCol sm="6">
            <div class="form-group">
              <label class="d-block">Housekeeping paid</label>
              <div class="d-flex align-items-center">
                <CSwitch
                  color="primary"
                  :checked.sync="itemReservationTemp.cleaningIsPaid"
                  aria-label="Housekeeping paid"
                />
                <span class="ml-2">{{ itemReservationTemp.cleaningIsPaid ? "Yes" : "No" }}</span>
              </div>
            </div>
          </CCol>
          <CCol sm="6">
            <CInput
              label="Housekeeping payment date"
              type="date"
              v-model="itemReservationTemp.cleaningPaymentDate"
            />
          </CCol>
        </CRow>

        <template #footer>
          <CButton
            v-if="itemReservationTemp.id"
            color="danger"
            variant="ghost"
            class="mr-auto"
            :disabled="savingReservation"
            @click="deleteReservation"
          >
            <CIcon name="cil-trash" /> Delete
          </CButton>
          <CButton color="light" :disabled="savingReservation" @click="cancelReservation">
            Cancel
          </CButton>
          <CButton color="primary" :disabled="savingReservation" @click="saveReservation">
            <CSpinner v-if="savingReservation" size="sm" class="mr-1" />
            {{ itemReservationTemp.id ? "Save" : "Add reservation" }}
          </CButton>
        </template>
      </CModal>
    </div>
    <div>
      <CModal
        size="lg"
        :show.sync="batchEditPopup"
        :close-on-backdrop="false"
        :centered="true"
      >
        <template #header>
          <h5 class="modal-title">
            Edit {{ selectedReservations.length }}
            {{ selectedReservations.length === 1 ? "reservation" : "reservations" }}
          </h5>
          <CButtonClose aria-label="Close" @click="batchEditPopup = false" />
        </template>
        <p class="text-muted">
          Switch on each field you want to change. Fields left off keep their current values.
        </p>
        <CRow class="align-items-center">
          <CCol sm="6">
            <div class="d-flex align-items-center mb-2">
              <CSwitch
                class="mr-2"
                color="primary"
                :checked.sync="batchEditData.editCleaningIsPaid"
                aria-label="Change housekeeping payment"
              />
              <span>Change housekeeping payment</span>
            </div>
          </CCol>
          <CCol sm="6">
            <div class="d-flex align-items-center mb-2">
              <CSwitch
                class="mr-2"
                color="primary"
                :disabled="!batchEditData.editCleaningIsPaid"
                :checked.sync="batchEditData.cleaningIsPaid"
                aria-label="Housekeeping paid"
              />
              <span>Housekeeping paid</span>
            </div>
          </CCol>
        </CRow>
        <hr />
        <CRow class="align-items-center">
          <CCol sm="6">
            <div class="d-flex align-items-center mb-2">
              <CSwitch
                class="mr-2"
                color="primary"
                :checked.sync="batchEditData.editCleaningCost"
                aria-label="Change housekeeping cost"
              />
              <span>Change housekeeping cost</span>
            </div>
          </CCol>
          <CCol sm="6">
            <CInput
              type="number"
              inputmode="decimal"
              prepend="RM"
              aria-label="Housekeeping cost"
              :disabled="!batchEditData.editCleaningCost"
              v-model="batchEditData.cleaningCost"
            />
          </CCol>
        </CRow>
        <hr />
        <CRow class="align-items-center">
          <CCol sm="6">
            <div class="d-flex align-items-center mb-2">
              <CSwitch
                class="mr-2"
                color="primary"
                :checked.sync="batchEditData.editLaundryIsPaid"
                aria-label="Change laundry payment"
              />
              <span>Change laundry payment</span>
            </div>
          </CCol>
          <CCol sm="6">
            <div class="d-flex align-items-center mb-2">
              <CSwitch
                class="mr-2"
                color="primary"
                :disabled="!batchEditData.editLaundryIsPaid"
                :checked.sync="batchEditData.laundryIsPaid"
                aria-label="Laundry paid"
              />
              <span>Laundry paid</span>
            </div>
          </CCol>
        </CRow>
        <hr />
        <CRow class="align-items-center">
          <CCol sm="6">
            <div class="d-flex align-items-center mb-2">
              <CSwitch
                class="mr-2"
                color="primary"
                :checked.sync="batchEditData.editLaundryCost"
                aria-label="Change laundry cost"
              />
              <span>Change laundry cost</span>
            </div>
          </CCol>
          <CCol sm="6">
            <CInput
              type="number"
              inputmode="decimal"
              prepend="RM"
              aria-label="Laundry cost"
              :disabled="!batchEditData.editLaundryCost"
              v-model="batchEditData.laundryCost"
            />
          </CCol>
        </CRow>

        <template #footer>
          <CButton color="light" :disabled="savingBatch" @click="batchEditPopup = false">
            Cancel
          </CButton>
          <CButton color="primary" :disabled="savingBatch" @click="saveBatchEdit">
            <CSpinner v-if="savingBatch" size="sm" class="mr-1" />
            Update {{ selectedReservations.length }}
            {{ selectedReservations.length === 1 ? "reservation" : "reservations" }}
          </CButton>
        </template>
      </CModal>

      <CModal
        size="lg"
        :show.sync="importFromICalPopup"
        :close-on-backdrop="false"
        :centered="true"
      >
        <template #header>
          <h5 class="modal-title">Import from iCal</h5>
          <CButtonClose aria-label="Close" @click="importFromICalPopup = false" />
        </template>
        <CAlert color="info" class="small">
          <div class="font-weight-bold mb-1">What happens</div>
          <ol class="mb-0 pl-3">
            <li>Choose the .ics file exported from Airbnb, Booking.com or another calendar.</li>
            <li>Pick the rental product the bookings belong to.</li>
            <li>Tick the bookings to import. Each one is added as a new reservation.</li>
          </ol>
        </CAlert>
        <CForm>
          <CRow form class="form-group">
            <CCol tag="label" sm="3" class="col-form-label" for="ical-file-input">
              iCal file
            </CCol>
            <CCol sm="9">
              <input
                id="ical-file-input"
                ref="icalFileInput"
                type="file"
                accept=".ics"
                @change="handleICalUpload"
                class="form-control mb-3"
              />
            </CCol>
          </CRow>

          <CSelect
            :value.sync="selectedIcalRentalProductId"
            :options="rentalProducts"
            label="Rental product"
            horizontal
          />
          <CDataTable
            :items="icalCalendarEvents"
            :fields="icalCalendarEventFields"
            items-per-page-select
            :items-per-page="itemsPerPage"
            hover
            striped
            small
            bordered
            pagination
            responsive
            @page-change="onPageChange"
          >
            <template #show_select-header>
              <CInputCheckbox
                :checked="isIcalAllSelected"
                aria-label="Select all bookings on this page"
                @change="toggleIcalSelectAll"
              />
            </template>

            <template #show_select="{ item }">
              <td class="py-2">
                <CInputCheckbox
                  :checked="selectedIcalItems.includes(item.id)"
                  :aria-label="`Select ${item.customerName}`"
                  @change="toggleSelectionICalItem(item.id)"
                />
              </td>
            </template>
            <template #startDate="{ item }">
              <td>{{ formatDate(item.startDate) }}</td>
            </template>
            <template #endDate="{ item }">
              <td>{{ formatDate(item.endDate) }}</td>
            </template>
          </CDataTable>
        </CForm>

        <template #footer>
          <CButton color="light" :disabled="importing" @click="importFromICalPopup = false">
            Cancel
          </CButton>
          <CButton
            color="primary"
            :disabled="importing || selectedIcalItems.length === 0"
            @click="saveIcalEvent"
          >
            <CSpinner v-if="importing" size="sm" class="mr-1" />
            Import {{ selectedIcalItems.length }}
            {{ selectedIcalItems.length === 1 ? "booking" : "bookings" }}
          </CButton>
        </template>
      </CModal>

      <!-- Delete confirmation (section 5.4) -->
      <CModal :show.sync="warningModal" :centered="true" :close-on-backdrop="!deleting">
        <template #header>
          <h5 class="modal-title">Delete {{ itemToDelete.customerName }}'s reservation?</h5>
        </template>
        <p class="mb-0">
          The booking for {{ itemToDelete.customerName }}
          <template v-if="itemToDelete.startDate">
            from {{ itemToDelete.startDate }} to {{ itemToDelete.endDate }}
          </template>
          will be removed and those days become free again. This can't be undone.
        </p>
        <template #footer>
          <CButton color="light" :disabled="deleting" @click="warningModal = false">
            Keep booking
          </CButton>
          <CButton color="danger" :disabled="deleting" @click="confirmDelete">
            <CSpinner v-if="deleting" size="sm" class="mr-1" />
            Delete reservation
          </CButton>
        </template>
      </CModal>

      <!-- Create bill modal -->
      <CModal
        size="xl"
        :show.sync="createBillPopup"
        :close-on-backdrop="false"
        :centered="true"
      >
        <template #header>
          <h5 class="modal-title">Create bill from reservations</h5>
          <CButtonClose aria-label="Close" @click="createBillPopup = false" />
        </template>
        <CForm>
          <CRow>
            <CCol sm="6">
              <CSelect
                label="Vendor *"
                :value.sync="billFormData.vendorId"
                :options="vendorOptions"
                description="The housekeeping or laundry supplier you're paying"
              />
            </CCol>
            <CCol sm="6">
              <CInput
                label="Bill date *"
                type="date"
                v-model="billFormData.date"
              />
            </CCol>
          </CRow>

          <CRow>
            <CCol sm="6" class="mb-3">
              <div class="small text-muted">Total</div>
              <div class="h5 mb-0">RM {{ formatMyr(billFormData.totalAmount) }}</div>
            </CCol>
          </CRow>

          <CRow>
            <CCol sm="12">
              <CTextarea
                label="Description"
                v-model="billFormData.description"
                rows="4"
                placeholder="Bill for housekeeping services..."
              />
            </CCol>
          </CRow>

          <CRow class="mt-3">
            <CCol sm="12">
              <h3 class="h6">
                Selected reservations ({{ selectedReservations.length }})
              </h3>
              <CDataTable
                :items="selectedReservationItems"
                :fields="billSummaryFields"
                striped
                small
                bordered
                responsive
              >
                <template #show_index="{ index }">
                  <td class="py-2">{{ index + 1 }}</td>
                </template>
                <template #dates="{ item }">
                  <td class="py-2">{{ item.startDate }} – {{ item.endDate }}</td>
                </template>
                <template #cleaningCost="{ item }">
                  <td class="py-2 text-right">{{ formatMyr(item.cleaningCost) }}</td>
                </template>
                <template #laundryCost="{ item }">
                  <td class="py-2 text-right">{{ formatMyr(item.laundryCost) }}</td>
                </template>
                <template #totalCost="{ item }">
                  <td class="py-2 text-right font-weight-bold">
                    {{ formatMyr((parseFloat(item.cleaningCost) || 0) + (parseFloat(item.laundryCost) || 0)) }}
                  </td>
                </template>
                <template #footer>
                  <tr>
                    <td colspan="5" class="text-right font-weight-bold">Total</td>
                    <td class="text-right font-weight-bold">
                      RM {{ formatMyr(billFormData.totalAmount) }}
                    </td>
                  </tr>
                </template>
              </CDataTable>
            </CCol>
          </CRow>
        </CForm>

        <template #footer>
          <CButton color="light" :disabled="savingBill" @click="createBillPopup = false">
            Cancel
          </CButton>
          <CButton
            color="primary"
            @click="saveBill"
            :disabled="!billFormData.vendorId || savingBill"
          >
            <CSpinner v-if="savingBill" size="sm" class="mr-1" />
            Create bill
          </CButton>
        </template>
      </CModal>
    </div>
  </div>
</template>

<script>
import BillApi from "@/lib/billApi";
import VendorApi from "@/lib/vendorApi";
import RentalReservationApi from "@/lib/rentalReservationApi";
import {
  CalendarView,
  CalendarViewHeader,
  CalendarMathMixin,
} from "vue-simple-calendar";
// The next two lines are processed by webpack. Both CSS files are optional theme files.
import "vue-simple-calendar/static/css/default.css";
import "vue-simple-calendar/static/css/holidays-us.css";
import RentalProductApi from "@/lib/rentalProductApi";
import html2canvas from "html2canvas";
import { asDateString, diffDays, formatDate, mytToday } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";

const currDate = new Date();

// Default filter range: 1 Jan two years ago → 31 Dec next year (Malaysia calendar year),
// so the default never hides upcoming bookings.
function defaultFilterRange() {
  const year = Number(mytToday().substring(0, 4));
  return { startDate: `${year - 2}-01-01`, endDate: `${year + 1}-12-31` };
}

export default {
  name: "RentalReservation",
  mixins: [CalendarMathMixin],
  components: {
    CalendarView,
    CalendarViewHeader,
  },
  data() {
    const range = defaultFilterRange();

    return {
      loading: false,
      error: false,
      deleting: false,
      savingReservation: false,
      savingBatch: false,
      importing: false,
      savingBill: false,
      reservationErrors: {},
      viewOptions: [
        { value: "year", label: "Year" },
        { value: "month", label: "Month" },
        { value: "week", label: "Week" },
      ],
      icalCalendarEventFields: [
        {
          key: "show_select",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
        { key: "customerName", label: "Guest" },
        { key: "startDate", label: "From" },
        { key: "endDate", label: "To" },
      ],
      icalCalendarEvents: [], // Store parsed events here
      importFromICalPopup: false, // Controls the modal visibility
      batchEditPopup: false, // Controls the modal visibility
      batchEditData: {
        editCleaningIsPaid: false,
        cleaningIsPaid: false, // Default value
        editCleaningCost: false,
        cleaningCost: 0, // Default value

        editLaundryIsPaid: false,
        laundryIsPaid: false, // Default value
        editLaundryCost: false,
        laundryCost: 0, // Default value
      },
      selectedIcalItems: [],
      currentPage: 1,
      itemsPerPage: 10,

      selectedReservations: [],
      createBillPopup: false,
      vendors: [],
      vendorOptions: [],
      billFormData: {
        vendorId: "",
        date: mytToday(),
        totalAmount: 0,
        description: "",
      },
      billSummaryFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:5%",
        },
        { key: "customerName", label: "Guest" },
        { key: "productName", label: "Rental product" },
        { key: "dates", label: "Dates" },
        { key: "cleaningCost", label: "Housekeeping (RM)", _classes: "text-right" },
        { key: "laundryCost", label: "Laundry (RM)", _classes: "text-right" },
        { key: "totalCost", label: "Total (RM)", _classes: "text-right" },
      ],
      filterCriteria: {
        rentalProductId: "",
        startDate: range.startDate,
        endDate: range.endDate,
        totalDays: "", // Filter for total days
        cleaningIsPaid: "", // Filter for cleaning payment status
        minDaysFilter: false, // false = exact match, true = minimum days filter
      },
      selectedIcalRentalProductId: "",
      fields: [
        {
          key: "show_select",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
        {
          key: "show_index",
          label: "#",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
        { key: "productName", label: "Rental product" },
        { key: "customerName", label: "Guest" },
        { key: "startDate", label: "From" },
        { key: "endDate", label: "To" },
        { key: "totalDays", label: "Days" },
        { key: "cleaningIsPaid", label: "Housekeeping" },
        { key: "cleaningCostInRM", label: "Housekeeping cost", _classes: "text-right" },
        { key: "laundryIsPaid", label: "Laundry" },
        { key: "laundryCostInRM", label: "Laundry cost", _classes: "text-right" },
        { key: "totalCostDisplay", label: "Total cost", _classes: "text-right" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
      ],
      itemReservationTemp: {},
      addReservationFormPopup: false,

      statuses: [],
      items: [],
      availability: {
        startDate: "",
        endDate: "",
        price: 0,
        status: 0,
      },
      filterRentalProducts: [],
      rentalProducts: [],
      showDate: currDate,
      displayPeriod: "month",
      events: [],
      useDefaultTheme: true,
      useHolidayTheme: false,
      coloredDays: {},
      api: new RentalReservationApi(),
      rentalProductApi: new RentalProductApi(),
      warningModal: false,
      itemToDelete: {},

      // Split View & UI Enhancement Properties
      showFilters: true, // Filter panel collapsed by default
      mobileView: 'list', // Default to list on mobile
      highlightedItemId: null, // For synchronized highlighting
      rentalProductsList: [], // For color legend
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Rental & Leasing", "Reservation"]);
    this.fetchStatuses();

    this.loadRentalProducts();
    this.refreshTable();
  },
  computed: {
    visibleIcalItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.icalCalendarEvents.slice(start, start + this.itemsPerPage);
    },
    isIcalAllSelected() {
      return (
        this.visibleIcalItems.length > 0 &&
        this.visibleIcalItems.every((item) =>
          this.selectedIcalItems.includes(item.id)
        )
      );
    },
    isAllSelected() {
      if (this.selectedReservations.length === 0) {
        return false; // No items selected
      }
      return this.computedItems.length === this.selectedReservations.length;
    },
    totalDays() {
      return this.computedItems.reduce((sum, item) => {
        return sum + (item.totalDays || 0);
      }, 0);
    },
    totalCost() {
      return this.totalLaundryCost + this.totalCleaningCost;
    },
    totalLaundryCost() {
      return this.computedItems.reduce((sum, item) => {
        return sum + (parseFloat(item.laundryCost) || 0);
      }, 0);
    },
    totalCleaningCost() {
      return this.computedItems.reduce((sum, item) => {
        return sum + (parseFloat(item.cleaningCost) || 0);
      }, 0);
    },
    selectedReservationItems() {
      return this.items
        .filter((item) => this.selectedReservations.includes(item.id))
        .map((item) => {
          return {
            id: item.id,
            customerName: item.customerName,
            productName: this.getProductName(item),
            startDate: this.getDisplayDate(item.startDate),
            endDate: this.getDisplayDate(item.endDate),
            cleaningCost: item.cleaningCost || 0,
            laundryCost: item.laundryCost || 0,
          };
        });
    },
    computedItems() {
      return this.items.map((item) => {
        return {
          ...item,
          productName: this.getProductName(item),
          startDate: this.getDisplayDate(item.startDate),
          endDate: this.getDisplayDate(item.endDate),
          totalDays: this.calculateTotalDays(item.startDate, item.endDate),
          cleaningIsPaidRaw: !!item.cleaningIsPaid,
          cleaningIsPaid: item.cleaningIsPaid ? "Paid" : "Unpaid",
          cleaningCostInRM: item.cleaningCost ? `RM ${formatMyr(item.cleaningCost)}` : "—",
          cleaningPaymentDate: item.cleaningPaymentDate
            ? this.getDisplayDate(item.cleaningPaymentDate)
            : "—",
          laundryIsPaidRaw: !!item.laundryIsPaid,
          laundryIsPaid: item.laundryIsPaid ? "Paid" : "Unpaid",
          laundryCostInRM: item.laundryCost ? `RM ${formatMyr(item.laundryCost)}` : "—",
          laundryPaymentDate: item.laundryPaymentDate
            ? this.getDisplayDate(item.laundryPaymentDate)
            : "—",
          totalCostDisplay: `RM ${formatMyr((parseFloat(item.cleaningCost) || 0) + (parseFloat(item.laundryCost) || 0))}`,
        };
      });
    },

    themeClasses() {
      return {
        "theme-default": this.useDefaultTheme,
        "holiday-us-traditional": this.useHolidayTheme,
        "holiday-us-official": this.useHolidayTheme,
      };
    },

    // Count active filters
    activeFilterCount() {
      let count = 0;
      if (this.filterCriteria.rentalProductId) count++;
      if (this.filterCriteria.cleaningIsPaid) count++;
      if (this.filterCriteria.totalDays) count++;
      // Don't count date range as they have defaults
      return count;
    },
  },
  methods: {
    formatDate,
    formatMyr,
    // Look up the API record (raw dates) behind a display row / calendar event.
    getRawItem(id) {
      return this.items.find((i) => i.id === id) || {};
    },
    confirmDelete() {
      var self = this;
      self.deleting = true;
      this.api
        .delete(self.itemToDelete.id)
        .then(() => {
          self.$toast.success("Reservation deleted");
          self.warningModal = false;
          if (self.itemReservationTemp.id === self.itemToDelete.id) {
            self.addReservationFormPopup = false;
            self.itemReservationTemp = {};
          }
          self.itemToDelete = {};
          self.refreshTable();
        })
        .catch(({ data }) => {
          self.$toast.error(
            `Couldn't delete the reservation. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          self.deleting = false;
        });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    onPageChange(page) {
      this.currentPage = page;
    },
    async handleICalUpload(event) {
      this.icalCalendarEvents = [];
      const file = event.target.files[0];
      if (!file) return;

      const text = await file.text();
      this.parseICal(text);
    },
    parseICal(icalText) {
      const events = [];
      const eventBlocks = icalText.split("BEGIN:VEVENT").slice(1);

      eventBlocks.forEach((block) => {
        const summaryMatch = block.match(/SUMMARY:(.*)/);
        const startMatch = block.match(/DTSTART(?:;[^:]+)?:([^\n\r]+)/);
        const endMatch = block.match(/DTEND(?:;[^:]+)?:([^\n\r]+)/);
        const uidMatch = block.match(/UID:([^\n\r]+)/); // Extract UID for unique ID

        // Convert an iCal date string (yyyyMMdd...) to a UTC-midnight Date
        const parseIcalDate = (dateStr) => {
          if (!dateStr) return null;
          const year = dateStr.substr(0, 4);
          const month = dateStr.substr(4, 2);
          const day = dateStr.substr(6, 2);
          return new Date(`${year}-${month}-${day}T00:00:00Z`);
        };

        const isAllDayEnd = block.includes("DTEND;VALUE=DATE");

        let startDate = startMatch ? parseIcalDate(startMatch[1].trim()) : null;
        let endDate = endMatch ? parseIcalDate(endMatch[1].trim()) : null;

        // If all-day event, DTEND is exclusive → minus 1 day
        if (isAllDayEnd && endDate) {
          endDate.setDate(endDate.getDate() - 1);
        }

        events.push({
          id: uidMatch ? uidMatch[1].trim() : "", // Use UID as id
          customerName: summaryMatch ? summaryMatch[1].trim() : "",
          startDate: startDate,
          endDate: endDate,
        });
      });

      // Newest first
      this.icalCalendarEvents = events.sort((a, b) => {
        return new Date(b.startDate) - new Date(a.startDate);
      });
    },
    importFromICal() {
      this.selectedIcalItems = []; // Reset selected items
      this.icalCalendarEvents = []; // Reset events
      // Clear the file input
      if (this.$refs.icalFileInput) {
        this.$refs.icalFileInput.value = "";
      }

      this.importFromICalPopup = true;
    },
    exportTableAsImage() {
      const tableElement = this.$el.querySelector(".reservation-table table") || this.$el.querySelector("table");
      if (!tableElement) {
        this.$toast.info("There are no reservations to export yet");
        return;
      }

      html2canvas(tableElement, { scale: 2 })
        .then((canvas) => {
          const image = canvas.toDataURL("image/png"); // Convert to PNG format

          // Create a download link
          const link = document.createElement("a");
          link.href = image;
          link.download = "Rental_Reservations.png"; // Set the file name
          link.click();
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error("Couldn't export the table. Try again.");
        });
    },

    saveIcalEvent() {
      if (this.selectedIcalItems.length === 0) {
        this.$toast.warning("Tick at least one booking to import");
        return;
      }

      const selectedEvents = this.icalCalendarEvents.filter((event) =>
        this.selectedIcalItems.includes(event.id)
      );

      // Map to plain-object DTOs
      const payload = selectedEvents.map((ev) => ({
        rentalProductId: this.selectedIcalRentalProductId, // must not be ""
        customerName: ev.customerName,
        startDate:
          ev.startDate instanceof Date
            ? ev.startDate.toISOString()
            : ev.startDate, // convert Date→string
        endDate:
          ev.endDate instanceof Date ? ev.endDate.toISOString() : ev.endDate, // convert Date→string
      }));

      this.importing = true;
      this.api
        .addBatch(payload)
        .then(() => {
          this.refreshTable();
          this.$toast.success(
            payload.length === 1 ? "1 booking imported" : `${payload.length} bookings imported`
          );
          this.importFromICalPopup = false;
        })
        .catch(({ data }) => {
          this.$toast.error(
            `Couldn't import the bookings. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.importing = false;
        });
    },

    saveBatchEdit() {
      const updatedReservations = this.selectedReservations.map((id) => ({
        id,
        editCleaningIsPaid: this.batchEditData.editCleaningIsPaid,
        cleaningIsPaid: this.batchEditData.cleaningIsPaid,
        editCleaningCost: this.batchEditData.editCleaningCost,
        cleaningCost: this.batchEditData.cleaningCost,

        editLaundryIsPaid: this.batchEditData.editLaundryIsPaid,
        laundryIsPaid: this.batchEditData.laundryIsPaid,
        editLaundryCost: this.batchEditData.editLaundryCost,
        laundryCost: this.batchEditData.laundryCost,
      }));

      this.savingBatch = true;
      this.api
        .updateBatch(updatedReservations)
        .then(() => {
          this.refreshTable();
          this.deselectAll();
          this.$toast.success(
            updatedReservations.length === 1
              ? "1 reservation updated"
              : `${updatedReservations.length} reservations updated`
          );
          this.batchEditPopup = false;
        })
        .catch(({ data }) => {
          this.$toast.error(
            `Couldn't update the reservations. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.savingBatch = false;
        });
    },

    calculateTotalDays(startDate, endDate) {
      if (!startDate || !endDate) return 0;
      return diffDays(endDate, startDate) + 1; // inclusive of both days
    },
    openBatchEditModal() {
      if (this.selectedReservations.length === 0) {
        this.$toast.warning("Select at least one reservation first");
        return;
      }

      // Pre-fill batch edit fields with the first selected reservation (if available)
      const firstReservation = this.items.find(
        (item) => item.id === this.selectedReservations[0]
      );

      this.batchEditData.editCleaningIsPaid = false;
      this.batchEditData.editCleaningCost = false;
      this.batchEditData.editLaundryIsPaid = false;
      this.batchEditData.editLaundryCost = false;

      if (firstReservation) {
        this.batchEditData.cleaningIsPaid = firstReservation.cleaningIsPaid;
        this.batchEditData.cleaningCost = firstReservation.cleaningCost || 0;
        this.batchEditData.laundryIsPaid = firstReservation.laundryIsPaid;
        this.batchEditData.laundryCost = firstReservation.laundryCost || 0;
      }

      this.batchEditPopup = true; // Show modal
    },
    toggleSelectionICalItem(id) {
      const index = this.selectedIcalItems.indexOf(id);
      if (index === -1) {
        this.selectedIcalItems.push(id);
      } else {
        this.selectedIcalItems.splice(index, 1);
      }
    },
    toggleIcalSelectAll() {
      const visibleIds = this.visibleIcalItems.map((i) => i.id);
      if (this.isIcalAllSelected) {
        // deselect them
        this.selectedIcalItems = this.selectedIcalItems.filter(
          (id) => !visibleIds.includes(id)
        );
      } else {
        // add any missing IDs
        this.selectedIcalItems = Array.from(
          new Set([...this.selectedIcalItems, ...visibleIds])
        );
      }
    },
    toggleSelection(id) {
      const index = this.selectedReservations.indexOf(id);
      if (index === -1) {
        this.selectedReservations = [...this.selectedReservations, id]; // Ensure reactivity
      } else {
        this.selectedReservations = this.selectedReservations.filter(
          (item) => item !== id
        );
      }
    },
    deselectAll() {
      this.selectedReservations = [];
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedReservations = []; // Deselect all items
      } else {
        this.$nextTick(() => {
          this.selectedReservations = this.computedItems.map((item) => item.id);
        });
      }
    },
    selectAllFiltered() {
      this.selectedReservations = []; // Reset first to force reactivity
      this.$nextTick(() => {
        this.selectedReservations = this.computedItems.map((item) => item.id);
      });
    },
    onCleanerClick() {
      const range = defaultFilterRange();
      // Reset to wide date range
      this.filterCriteria = {
        rentalProductId: "",
        startDate: range.startDate,
        endDate: range.endDate,
        totalDays: "",
        cleaningIsPaid: "",
        minDaysFilter: false,
      };

      this.refreshTable();
    },
    getDisplayDate(dt) {
      return formatDate(dt);
    },

    // Fill the reservation form from the API record (raw "yyyy-MM-dd..." dates, not display strings)
    fillReservationForm(raw) {
      this.itemReservationTemp = {
        id: raw.id,
        rentalProductId: raw.rentalProductId,
        startDate: asDateString(raw.startDate),
        endDate: asDateString(raw.endDate),
        price: raw.price,
        customerName: raw.customerName,
        customerPhone: raw.customerPhone,
        cleaningVendorId: raw.cleaningVendorId,
        cleaningCost: raw.cleaningCost,
        cleaningIsPaid: raw.cleaningIsPaid,
        cleaningPaymentDate: asDateString(raw.cleaningPaymentDate),
      };
      this.reservationErrors = {};
    },

    openAddReservation() {
      const today = mytToday();
      this.itemReservationTemp = {
        rentalProductId: this.rentalProducts.length ? this.rentalProducts[0].value : "",
        startDate: today,
        endDate: today,
        price: 0,
        customerName: "",
        customerPhone: "",
        cleaningCost: 0,
        cleaningIsPaid: false,
        cleaningPaymentDate: "",
      };
      this.reservationErrors = {};
      this.addReservationFormPopup = true;
    },

    onEdit(item) {
      this.fillReservationForm(this.getRawItem(item.id));
      this.addReservationFormPopup = true;
    },

    onDuplicate(item) {
      // Copy without the ID so it is saved as a new reservation
      this.fillReservationForm(this.getRawItem(item.id));
      delete this.itemReservationTemp.id;
      this.itemReservationTemp.customerName = (item.customerName || "") + " (copy)";
      this.itemReservationTemp.cleaningIsPaid = false; // Reset payment status
      this.itemReservationTemp.cleaningPaymentDate = "";
      this.addReservationFormPopup = true;
    },

    getProductName(item) {
      if (item.rentalProduct) {
        return item.rentalProduct.name;
      }
      return "—";
    },

    deleteReservation() {
      // Confirm through the shared delete modal (section 5.4)
      const t = this.itemReservationTemp;
      this.itemToDelete = {
        id: t.id,
        customerName: t.customerName,
        startDate: formatDate(t.startDate),
        endDate: formatDate(t.endDate),
      };
      this.warningModal = true;
    },
    cancelReservation() {
      // Close the modal without saving any changes
      this.itemReservationTemp = {};
      this.addReservationFormPopup = false;
      this.availability.startDate = "";
      this.availability.endDate = "";

      // Remove previous temporary event
      this.events = this.events.filter((event) => !event.tempEvent);
    },

    validateReservation() {
      const t = this.itemReservationTemp;
      const errors = {};
      if (!t.startDate) errors.startDate = "Choose the check-in day";
      if (!t.endDate) errors.endDate = "Choose the check-out day";
      else if (t.startDate && t.endDate < t.startDate)
        errors.endDate = "The last day must be on or after the first day";
      if (!t.customerName || !String(t.customerName).trim())
        errors.customerName = "Enter the guest's name";
      this.reservationErrors = errors;
      return Object.keys(errors).length === 0;
    },

    saveReservation() {
      if (this.savingReservation) return;
      if (!this.validateReservation()) return;
      const isUpdate = !!this.itemReservationTemp.id;
      const request = isUpdate
        ? this.api.update(this.itemReservationTemp)
        : this.api.create(this.itemReservationTemp);
      this.savingReservation = true;
      request
        .then(() => {
          this.refreshTable();
          this.$toast.success(isUpdate ? "Reservation saved" : "Reservation added");
          this.addReservationFormPopup = false;
          this.itemReservationTemp = {};
          this.events = this.events.filter((event) => !event.tempEvent);
        })
        .catch(({ data }) => {
          this.$toast.error(
            `Couldn't save the reservation. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.savingReservation = false;
        });
    },

    getClassByColor(colorName) {
      return (colorName || "").toLowerCase();
    },

    fetchStatuses() {
      var self = this;
      self.api
        .getStatus()
        .then((response) => {
          this.statuses = response.result.map((state) => ({
            value: state.id,
            label: state.name,
          }));
        })
        .catch((err) => {
          console.error(err);
        });
    },

    refreshTable() {
      var self = this;
      self.loading = true;
      self.error = false;

      const params = {
        rentalProductId: this.filterCriteria.rentalProductId,
        startDate: this.filterCriteria.startDate,
        endDate: this.filterCriteria.endDate,
        totalDays: this.filterCriteria.totalDays || 0, // Pass totalDays if set
        cleaningIsPaid: this.filterCriteria.cleaningIsPaid, // Pass cleaningIsPaid if selected
        minDaysFilter: this.filterCriteria.minDaysFilter,
      };

      self.api
        .getList(params)
        .then((response) => {
          self.items = response.result || [];
          // An empty result is shown by the empty state; no toast.
          this.events = self.items.map((a) => ({
            title: a.customerName,
            startDate: new Date(a.startDate),
            endDate: new Date(a.endDate),
            allDay: true, // Ensures the full day is covered
            classes: this.getClassByColor(a.rentalProduct && a.rentalProduct.colorName),
            id: a.id,
          }));
        })
        .catch((err) => {
          console.error(err);
          self.error = true;
          if (self.items.length) {
            self.$toast.error("Couldn't refresh reservations. Check your connection and try again.");
          }
        })
        .finally(() => {
          self.loading = false;
        });
    },

    loadRentalProducts() {
      this.rentalProductApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.filterRentalProducts = [
            { value: "", label: "All products" },
            ...response.result.map((p) => ({
              value: p.id,
              label: p.name,
            })),
          ];

          // Plain names: colour is shown in the calendar legend, not with emoji.
          this.rentalProducts = response.result.map((p) => ({
            value: p.id,
            label: p.colorName ? `${p.name} (${p.colorName.toLowerCase()})` : p.name,
          }));

          // Colour legend (product colours are stored data)
          this.rentalProductsList = response.result.map((p) => ({
            id: p.id,
            name: p.name,
            colorCode: p.colorCode || "#007bff",
            colorName: p.colorName || "Blue",
          }));

          if (this.rentalProducts.length) {
            this.selectedIcalRentalProductId = this.rentalProducts[0].value;
          }
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error("Couldn't load your rental products. Refresh the page to try again.");
        });
    },
    setShowDate(d) {
      this.showDate = d;
    },
    onClickDay(date) {
      // Calendar day as "yyyy-MM-dd" in Malaysia time (no UTC drift)
      const localDate = asDateString(date);

      // If start date is not set, set it first
      if (!this.availability.startDate) {
        this.availability.startDate = localDate;
        this.events.push({
          title: "Selected",
          startDate: new Date(this.availability.startDate),
          endDate: new Date(this.availability.startDate),
          tempEvent: true, // Mark as temporary event
        });
      }
      // If start date is set but end date is not, set the end date
      else if (!this.availability.endDate) {
        if (localDate >= this.availability.startDate) {
          this.availability.endDate = localDate;
          this.events = this.events.filter((event) => !event.tempEvent);
          this.events.push({
            title: "Selected",
            startDate: new Date(this.availability.startDate),
            endDate: new Date(this.availability.endDate),
            tempEvent: true, // Mark as temporary event
          });

          this.openAddReservation();
          this.itemReservationTemp.startDate = this.availability.startDate;
          this.itemReservationTemp.endDate = this.availability.endDate;
          this.availability.startDate = "";
          this.availability.endDate = "";
        } else {
          this.availability.startDate = localDate;
          this.availability.endDate = "";

          // Remove previous temporary event
          this.events = this.events.filter((event) => !event.tempEvent);

          this.events.push({
            title: "Selected",
            startDate: new Date(this.availability.startDate),
            endDate: new Date(this.availability.startDate),
            tempEvent: true, // Mark as temporary event
          });
        }
      }
      // If both are already set, reset and start over
      else {
        this.availability.startDate = "";
        this.availability.endDate = "";

        // Remove previous temporary event
        this.events = this.events.filter((event) => !event.tempEvent);
      }
    },
    onClickEvent(e) {
      if (!e.originalEvent || !e.originalEvent.id) return;
      // Highlight corresponding row in list
      this.highlightedItemId = e.originalEvent.id;
      this.scrollToListItem(e.originalEvent.id);

      this.fillReservationForm(this.getRawItem(e.originalEvent.id));
      this.addReservationFormPopup = true;
    },

    // Synchronized highlighting
    highlightListRow(id) {
      this.highlightedItemId = id;
    },

    scrollToListItem(id) {
      this.$nextTick(() => {
        const row = this.$refs[`row-${id}`];
        if (row && row[0]) {
          row[0].scrollIntoView({ block: "center" });
        }
      });
    },
    onDrop(event, date) {
      // Determine the delta between the old start date and the date chosen,
      // and apply that delta to both the start and end date to move the event.
      const eLength = this.dayDiff(event.startDate, date);
      event.originalEvent.startDate = this.addDays(event.startDate, eLength);
      event.originalEvent.endDate = this.addDays(event.endDate, eLength);
    },

    // Bill creation
    openCreateBillModal() {
      if (this.selectedReservations.length === 0) {
        this.$toast.warning("Select at least one reservation first");
        return;
      }

      // Load vendors first
      this.loadVendors();

      // Calculate total amount (cleaning + laundry costs)
      const total = this.selectedReservationItems.reduce((sum, item) => {
        return (
          sum +
          (parseFloat(item.cleaningCost) || 0) +
          (parseFloat(item.laundryCost) || 0)
        );
      }, 0);

      const count = this.selectedReservations.length;
      const description = `Bill for ${count} ${count === 1 ? "reservation" : "reservations"}`;

      this.billFormData = {
        vendorId: "",
        date: mytToday(),
        totalAmount: total.toFixed(2),
        description: description,
      };

      this.createBillPopup = true;
    },

    loadVendors() {
      var self = this;
      const vendorApi = new VendorApi();
      vendorApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.vendors = response.result;
          self.vendorOptions = [
            { value: "", label: "Choose a vendor", disabled: true },
            ...response.result.map((v) => ({
              value: v.id,
              label: v.name,
            })),
          ];
        })
        .catch((err) => {
          console.error(err);
          self.$toast.error("Couldn't load your vendors. Close this window and try again.");
        });
    },

    saveBill() {
      var self = this;
      if (!this.billFormData.vendorId) {
        this.$toast.warning("Choose a vendor first");
        return;
      }

      const billData = {
        vendorId: this.billFormData.vendorId,
        date: this.billFormData.date,
        totalAmount: parseFloat(this.billFormData.totalAmount),
        description: this.billFormData.description,
        isPaid: false,
      };

      self.savingBill = true;
      const billApi = new BillApi();
      billApi
        .create(billData)
        .then((response) => {
          self.$toast.success("Bill created");
          self.createBillPopup = false;
          self.deselectAll();

          // Navigate to the created bill
          self.$router.push({
            name: "BillById",
            params: { id: response.result.id },
          });
        })
        .catch(({ data }) => {
          self.$toast.error(
            `Couldn't create the bill. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          self.savingBill = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/scss/style";
#calendar {
  color: $body-color;
  height: 100vh;
  margin-left: auto;
  margin-right: auto;
}
.theme-default .cv-event {
  background-color: $gray-300;
  border-color: $table-border-color;
  color: $body-color;
}

// Product calendar colours. These mirror the colours a user picks for each rental product
// (RentalProduct.colorCode / colorName are stored data), so they are not theme tokens.
.theme-default .cv-event.red {
  background-color: #ff5733;
  border-color: lighten(#ff5733, 5%);
  color: $white;
}
.theme-default .cv-event.blue {
  background-color: #007bff;
  border-color: lighten(#007bff, 5%);
  color: $white;
}
.theme-default .cv-event.green {
  background-color: #28a745;
  border-color: lighten(#28a745, 5%);
  color: $white;
}
.theme-default .cv-event.yellow {
  background-color: #ffc107;
  border-color: lighten(#ffc107, 5%);
}
.theme-default .cv-event.purple {
  background-color: #6f42c1;
  border-color: lighten(#6f42c1, 5%);
  color: $white;
}
.theme-default .cv-event.black {
  background-color: #343a40;
  border-color: lighten(#343a40, 5%);
  color: $white;
}
.theme-default .cv-event.white {
  background-color: #f8f9fa;
  border-color: darken(#f8f9fa, 15%);
}
.theme-default .cv-event.orange {
  background-color: #fd7e14;
  border-color: lighten(#fd7e14, 5%);
}
.theme-default .cv-event.brown {
  background-color: #795548;
  border-color: lighten(#795548, 5%);
  color: $white;
}
.theme-default .cv-event.cyan {
  background-color: #17a2b8;
  border-color: lighten(#17a2b8, 5%);
  color: $white;
}
.theme-default .cv-event.amber {
  background-color: #ffbf00;
  border-color: lighten(#ffbf00, 5%);
}

.theme-default .cv-day.today {
  background-color: $gray-300;
}
.theme-default .cv-day.today > .cv-day-number {
  background-color: $primary;
  color: $white;
  border-radius: 50%;
  position: absolute;
  left: 0;
}
.theme-default .cv-day.past {
  background-color: $body-bg;
}
.theme-default .cv-day.outsideOfMonth {
  background-color: $body-bg;
}

/* ===== Split view layout ===== */
.split-view-container {
  display: flex;
  gap: 1rem;
  min-height: calc(100vh - 300px);
}

.calendar-panel {
  flex: 0 0 33.33%; /* lg="4" = 33.33% */
  min-width: 300px;
  max-width: 450px;
}

.calendar-panel .card {
  position: sticky;
  top: 1rem;
}

.list-panel {
  flex: 1;
  min-width: 0;
}

/* Calendar height for the side view */
.calendar-panel #calendar {
  height: 60vh;
  min-height: 500px;
}

@media (max-width: 1199px) {
  .calendar-panel {
    flex: 0 0 41.67%; /* md="5" = 41.67% */
  }
}

@media (max-width: 991px) {
  .split-view-container {
    flex-direction: column;
    min-height: auto;
  }

  .calendar-panel {
    max-width: none;
  }

  .calendar-panel #calendar {
    height: 50vh;
    min-height: 400px;
  }

  .calendar-panel .card {
    position: relative;
    top: 0;
  }
}

@media (max-width: 767px) {
  .calendar-panel {
    min-width: 0;
  }
}

/* ===== Colour legend ===== */
.color-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem;
  background: $gray-100;
  border-radius: $border-radius;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  background: $white;
  border-radius: $border-radius;
  white-space: nowrap;
}

.color-dot {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ===== Table ===== */
.table-wrapper {
  max-height: calc(100vh - 400px);
  overflow-y: auto;
}

.reservation-table {
  thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background: $white;
  }

  td,
  th {
    vertical-align: middle;
  }

  .action-buttons {
    white-space: nowrap;
  }
}

/* ===== Row highlighting (row picked on the calendar) ===== */
.highlighted-row {
  background-color: rgba($primary, 0.1);
}

/* ===== Action buttons footer ===== */
.gap-2 {
  gap: 0.5rem;
}
.theme-default .cv-weeks,
.theme-default .cv-day {
  border-color: $table-border-color;
}
.theme-default .cv-header,
.theme-default .cv-header-days,
.theme-default .cv-header-day {
  background-color: $gray-200;
  border-color: $table-border-color;
  border-style: solid;
}
.theme-default .cv-header-nav > button {
  color: $body-color;
  background-color: $input-bg;
  border-color: $input-border-color;
}
.theme-default .cv-day.draghover {
  outline: 2px solid $primary;
  outline-offset: -2px;
}
</style>
